import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Popconfirm, notification, Tag, Tooltip, Alert } from 'antd';
import { 
  DesktopOutlined, 
  MobileOutlined, 
  ChromeOutlined, 
  AppleOutlined, 
  AndroidOutlined, 
  WindowsOutlined, 
  GlobalOutlined, 
  LogoutOutlined, 
  KeyOutlined 
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import api from '../api';

dayjs.extend(relativeTime);

export default function SessionSettings() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const resp = await api.get('/settings/sessions');
      if (resp.data?.success) {
        setSessions(resp.data.sessions || []);
      } else {
        notification.error({ message: 'Failed to load sessions' });
      }
    } catch (e) {
      notification.error({ message: 'Error loading sessions', description: e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (id, isCurrent) => {
    try {
      const resp = await api.delete(`/settings/sessions/${id}`);
      if (resp.data?.success) {
        notification.success({ message: 'Session Revoked successfully' });
        if (isCurrent) {
          // Logged out of current session, redirect
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          sessionStorage.clear();
          window.location.href = '/';
        } else {
          fetchSessions();
        }
      } else {
        notification.error({ message: 'Failed to revoke session' });
      }
    } catch (e) {
      notification.error({ message: 'Error revoking session', description: e.message });
    }
  };

  const handleLogoutAllOther = async () => {
    try {
      const resp = await api.post('/settings/sessions/logout-all');
      if (resp.data?.success) {
        notification.success({ 
          message: 'Sessions Terminated', 
          description: resp.data.message || 'Logged out of all other devices successfully.' 
        });
        fetchSessions();
      } else {
        notification.error({ message: 'Failed to terminate other sessions' });
      }
    } catch (e) {
      notification.error({ message: 'Error terminating sessions', description: e.message });
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  // Helper to parse User-Agent for display
  const getDeviceDetails = (ua) => {
    const uaString = ua.toLowerCase();
    let os = 'Unknown OS';
    let osIcon = <DesktopOutlined style={{ fontSize: '18px', color: '#8c8c8c' }} />;
    let browser = 'Unknown Browser';
    let browserIcon = <GlobalOutlined style={{ fontSize: '18px', color: '#8c8c8c' }} />;

    // Detect OS
    if (uaString.includes('windows')) {
      os = 'Windows OS';
      osIcon = <WindowsOutlined style={{ fontSize: '18px', color: '#1890ff' }} />;
    } else if (uaString.includes('macintosh') || uaString.includes('mac os')) {
      os = 'macOS';
      osIcon = <AppleOutlined style={{ fontSize: '18px', color: '#000000' }} />;
    } else if (uaString.includes('android')) {
      os = 'Android';
      osIcon = <AndroidOutlined style={{ fontSize: '18px', color: '#52c41a' }} />;
    } else if (uaString.includes('iphone') || uaString.includes('ipad')) {
      os = 'iOS (iPhone/iPad)';
      osIcon = <AppleOutlined style={{ fontSize: '18px', color: '#000000' }} />;
    } else if (uaString.includes('linux')) {
      os = 'Linux OS';
    }

    // Detect Browser
    if (uaString.includes('chrome') || uaString.includes('crios')) {
      browser = 'Google Chrome';
      browserIcon = <ChromeOutlined style={{ fontSize: '18px', color: '#faad14' }} />;
    } else if (uaString.includes('safari') && !uaString.includes('chrome')) {
      browser = 'Apple Safari';
      browserIcon = <GlobalOutlined style={{ fontSize: '18px', color: '#096dd9' }} />;
    } else if (uaString.includes('firefox')) {
      browser = 'Mozilla Firefox';
      browserIcon = <GlobalOutlined style={{ fontSize: '18px', color: '#d4380d' }} />;
    } else if (uaString.includes('edge')) {
      browser = 'Microsoft Edge';
    } else if (uaString.includes('mobile-apk') || uaString.includes('okhttp')) {
      browser = 'Mobile App (APK)';
      browserIcon = <MobileOutlined style={{ fontSize: '18px', color: '#13c2c2' }} />;
    }

    return { os, osIcon, browser, browserIcon };
  };

  const columns = [
    {
      title: 'Device / Client',
      key: 'device',
      render: (_, record) => {
        const details = getDeviceDetails(record.userAgent);
        return (
          <Space size="middle">
            <Tooltip title={details.os}>{details.osIcon}</Tooltip>
            <Tooltip title={details.browser}>{details.browserIcon}</Tooltip>
            <div>
              <div style={{ fontWeight: 600 }}>{details.browser}</div>
              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{details.os}</div>
            </div>
          </Space>
        );
      }
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      render: (ip) => <Tag color="blue">{ip === '::1' || ip === '127.0.0.1' ? 'Localhost' : ip}</Tag>
    },
    {
      title: 'Last Activity',
      key: 'lastActivityAt',
      render: (_, record) => {
        const timeStr = dayjs(record.lastActivityAt).fromNow();
        return (
          <div>
            <div>{timeStr}</div>
            <div style={{ fontSize: '11px', color: '#bfbfbf' }}>
              Logged in: {dayjs(record.createdAt).format('DD MMM YYYY, hh:mm A')}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        if (record.isCurrent) {
          return <Tag color="green" style={{ fontWeight: 'bold' }}>Current Device</Tag>;
        }
        return <Tag color="default">Active Session</Tag>;
      }
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Popconfirm
          title="Are you sure you want to log out of this device?"
          onConfirm={() => handleRevoke(record.id, record.isCurrent)}
          okText="Yes, Logout"
          cancelText="No"
          disabled={record.isCurrent}
        >
          <Button 
            type="text" 
            danger 
            icon={<LogoutOutlined />} 
            disabled={record.isCurrent}
          >
            Logout
          </Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <Card 
      title={
        <Space>
          <KeyOutlined style={{ color: '#1890ff' }} />
          <span>Active Sessions & Device Management</span>
        </Space>
      }
      extra={
        sessions.length > 1 && (
          <Popconfirm
            title="Log out of all other active sessions? You will stay logged in here."
            onConfirm={handleLogoutAllOther}
            okText="Yes, Logout Others"
            cancelText="No"
          >
            <Button type="primary" danger icon={<LogoutOutlined />}>
              Logout All Other Devices
            </Button>
          </Popconfirm>
        )
      }
      style={{ margin: '24px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
    >
      <Alert
        message="Security Info"
        description="Here is the list of browsers and devices that have accessed your Thinktech account. You can instantly log out of any active session on any browser or mobile phone."
        type="info"
        showIcon
        style={{ marginBottom: '20px' }}
      />
      <Table 
        columns={columns} 
        dataSource={sessions.map(s => ({ ...s, key: s.id }))} 
        loading={loading}
        pagination={false}
        locale={{ emptyText: 'No active sessions found' }}
      />
    </Card>
  );
}
